import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Stack,
  Chip,
  Avatar,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import { FiX, FiDatabase, FiZap, FiAlertTriangle } from "react-icons/fi";

import {
  formatZman,
  formatRecurrence,
  formatWeekDays,
  formatPause,
  formatBool,
  dash,
} from "./scheduleFormat";

/**
 * The "View" dialog behind every row of the Overview ledger.
 *
 * It deliberately renders from the row object the list ALREADY has rather than
 * refetching. Both feeds carry everything a detail view needs:
 *
 *  - Upcoming (`/api/calender/simulate/all`, admin + web) attaches the whole
 *    `schedule.when`, the actions with resolved device names, and the room /
 *    device summaries to every simulated run.
 *  - History (`/api/historylogs/search`) stores its own `when` SNAPSHOT on the
 *    row — the rule as it was when that run fired — plus the per-device
 *    execution outcome.
 *
 * Refetching would also have been wrong twice over: the admin schedule route is
 * gated on role `admin` alone, while this page is reachable by role `user` too
 * (which would 403), and a history row must show the rule THAT RAN, not the
 * rule as it has since been edited.
 *
 * It is MUI rather than the shared Tailwind `ScheduleDetailModal` because this
 * page is MUI end to end; the Tailwind dialog sat visibly outside the page's
 * design, and its scroll lock targets `document.body` while this layout
 * scrolls an inner `<main>`, so the page kept scrolling underneath it.
 */

// Mirrors HEBREW_MONTH_INDEX in
// utils/userUtils/compute/computeHebrew.js (1-based).
const HEBREW_MONTHS = [
  null, "Nisan", "Iyar", "Sivan", "Tammuz", "Av", "Elul", "Tishrei",
  "Cheshvan", "Kislev", "Tevet", "Shevat", "Adar I", "Adar II",
];

const GREGORIAN_MONTHS = [
  null, "January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December",
];

/**
 * The time this ROW is about.
 *
 * ⚠️ For a simulated run that is the top-level `nextRunAt`. `when.nextRunAt` is
 * the schedule's single next fire time, which is identical on every occurrence
 * of the same schedule — reading it here would print the same timestamp on
 * every row. For a history row it is `lastRunAt`, when the run actually fired.
 */
export function resolveRunAt(item) {
  return item?.nextRunAt || item?.lastRunAt || item?.when?.nextRunAt || null;
}

const STATUS_COLORS = {
  success: "#10b981",
  completed: "#10b981",
  active: "#10b981",
  failed: "#ef4444",
  error: "#ef4444",
  skipped: "#f59e0b",
  paused: "#f59e0b",
  processing: "#6366f1",
  draft: "#94a3b8",
  archived: "#94a3b8",
};

function statusColor(value) {
  return STATUS_COLORS[String(value || "").toLowerCase()] || "#64748b";
}

function Field({ label, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          fontWeight: 900,
          letterSpacing: "0.06em",
          color: "#94a3b8",
          fontSize: "0.62rem",
        }}
      >
        {String(label).toUpperCase()}
      </Typography>
      <Typography
        variant="body2"
        sx={{ mt: 0.4, fontWeight: 600, color: "#1e293b", wordBreak: "break-word" }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: "block",
        mb: 1.5,
        fontWeight: 900,
        letterSpacing: "0.08em",
        color: "#64748b",
        fontSize: "0.65rem",
      }}
    >
      {String(children).toUpperCase()}
    </Typography>
  );
}

/** A responsive field grid without MUI <Grid>, which this file does not need. */
function FieldGrid({ children }) {
  return (
    <Box
      sx={{
        display: "grid",
        gap: 2.5,
        gridTemplateColumns: {
          xs: "repeat(2, minmax(0, 1fr))",
          md: "repeat(4, minmax(0, 1fr))",
        },
      }}
    >
      {children}
    </Box>
  );
}

function Banner({ tone = "amber", children }) {
  const tones = {
    amber: { bg: "#fffbeb", border: "#fde68a", color: "#92400e" },
    indigo: { bg: "#eef2ff", border: "#c7d2fe", color: "#3730a3" },
    red: { bg: "#fef2f2", border: "#fecaca", color: "#991b1b" },
  };
  const t = tones[tone] || tones.amber;
  return (
    <Box
      sx={{
        mb: 2.5,
        px: 2,
        py: 1.25,
        borderRadius: "12px",
        bgcolor: t.bg,
        border: `1px solid ${t.border}`,
        color: t.color,
        fontSize: "0.82rem",
        fontWeight: 600,
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      {children}
    </Box>
  );
}

export default function OverviewScheduleDialog({
  open,
  onClose,
  item,
  mode,
  themeColor,
  formatDisplay,
}) {
  if (!item) return null;

  const isHistory = mode === "history";
  const when = item.when || {};
  const scheduleType = when.scheduleType || item.scheduleType;
  const isHebrew = when.calenderType === "hebrew";
  const runAt = resolveRunAt(item);
  const pauseText = formatPause(item.pauseSummary ?? item.pause);
  const actions = item.actions || [];

  // `formatRecurrence` reads when.weekDaysBitmap; the simulator puts the
  // bitmap at the top level of a run, so hand it both.
  const recurrence = formatRecurrence({
    when: {
      ...when,
      scheduleType,
      weekDaysBitmap: when.weekDaysBitmap ?? item.weekDaysBitmap,
    },
  });

  const time = formatZman(when.zmanim) || dash(when.exactTime);

  const hebrewDate =
    when.hebrew?.month || when.hebrew?.monthDay
      ? `${HEBREW_MONTHS[when.hebrew.month] || `Month ${when.hebrew.month}`} ${
          when.hebrew.monthDay ?? ""
        }`.trim()
      : null;

  const gregorianDate =
    when.gregorian?.month || when.gregorian?.monthDay
      ? `${GREGORIAN_MONTHS[when.gregorian.month] || `Month ${when.gregorian.month}`} ${
          when.gregorian.monthDay ?? ""
        }`.trim()
      : null;

  const weekDays = formatWeekDays(when.weekDaysBitmap ?? item.weekDaysBitmap);
  const statusValue = item.cronstatus || item.status;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      // `scroll="paper"` keeps the header pinned and scrolls the body, and MUI
      // locks the real scroll container for us — unlike the Tailwind modal,
      // which locks document.body while this layout scrolls an inner <main>.
      scroll="paper"
      slotProps={{
        paper: {
          sx: { borderRadius: "20px", overflow: "hidden", maxHeight: "88vh" },
        },
      }}
    >
      {/* --- HEADER: mirrors the page's dark profile bar --- */}
      <Box sx={{ p: 2.5, bgcolor: "#0f172a", color: "white" }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Avatar
            sx={{
              bgcolor: themeColor,
              color: "white",
              borderRadius: "10px",
              width: 40,
              height: 40,
              flexShrink: 0,
            }}
          >
            {isHistory ? <FiDatabase size={18} /> : <FiZap size={18} />}
          </Avatar>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={800} lineHeight={1.25}>
              {item.name || item.scheduleName || "Schedule"}
              {item.smartRestore && (
                <Box component="span" sx={{ ml: 0.75 }} title="Smart Restore is on">
                  ✨
                </Box>
              )}
            </Typography>

            {item.scheduleId && (
              <Typography
                variant="caption"
                sx={{
                  fontFamily: "monospace",
                  opacity: 0.55,
                  wordBreak: "break-all",
                }}
              >
                {String(item.scheduleId)}
              </Typography>
            )}

            <Stack direction="row" spacing={1} mt={1.25} flexWrap="wrap" useFlexGap>
              {statusValue && (
                <Chip
                  label={statusValue}
                  size="small"
                  sx={{
                    fontWeight: 900,
                    fontSize: "0.6rem",
                    bgcolor: statusColor(statusValue),
                    color: "white",
                  }}
                />
              )}
              {scheduleType && (
                <Chip
                  label={scheduleType}
                  size="small"
                  sx={{
                    fontWeight: 800,
                    fontSize: "0.6rem",
                    bgcolor: "rgba(255,255,255,0.12)",
                    color: "white",
                  }}
                />
              )}
              {when.calenderType && (
                <Chip
                  label={when.calenderType}
                  size="small"
                  sx={{
                    fontWeight: 800,
                    fontSize: "0.6rem",
                    bgcolor: "rgba(255,255,255,0.12)",
                    color: "white",
                  }}
                />
              )}
            </Stack>
          </Box>

          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close"
            sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "white" } }}
          >
            <FiX size={18} />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ p: 3, bgcolor: "#f8fafc" }}>
        {pauseText && (
          <Banner tone="amber">
            <FiAlertTriangle size={14} /> {pauseText}
          </Banner>
        )}

        {/* The simulator answers "is this particular occurrence paused" per
            run, which a schedule-level pause summary cannot tell you. */}
        {item.pauseStatusOnDate && item.pauseStatusOnDate !== "none" && (
          <Banner tone="amber">
            This occurrence is affected by a pause ({item.pauseStatusOnDate}).
          </Banner>
        )}

        {isHistory && (
          <Banner tone="indigo">
            This is the rule as it was WHEN THIS RUN FIRED. The schedule may have
            been edited since.
          </Banner>
        )}

        <SectionTitle>Timing</SectionTitle>
        <FieldGrid>
          <Field label="Recurrence">{recurrence}</Field>
          <Field label="Time">{time}</Field>
          <Field label="Timezone">{dash(when.timezone)}</Field>
          <Field label="Calendar">{dash(when.calenderType)}</Field>

          <Field label={isHistory ? "Ran at" : "Fires at"}>
            {runAt ? formatDisplay(runAt, isHebrew) : "—"}
          </Field>
          <Field label="Round to closest">
            {formatBool(item.roundtoclosest)}
          </Field>
          {weekDays && <Field label="On days">{weekDays}</Field>}
          {hebrewDate && <Field label="Hebrew date">{hebrewDate}</Field>}
          {gregorianDate && <Field label="Gregorian date">{gregorianDate}</Field>}
          {when.special?.type && (
            <Field label="Special">{dash(when.special.type)}</Field>
          )}
        </FieldGrid>

        {/* A history row records BOTH times. When they disagree the run was
            late or early, which is exactly what `shouldSkipApiCall` reacts to —
            so never collapse them into one figure. */}
        {isHistory && when.nextRunAt && (
          <>
            <Divider sx={{ my: 2.5 }} />
            <FieldGrid>
              <Field label="Was planned for">
                {formatDisplay(when.nextRunAt, isHebrew)}
              </Field>
              <Field label="Outcome">{dash(item.cronstatus)}</Field>
            </FieldGrid>
          </>
        )}

        {!isHistory && (item.deviceSummary || item.roomSummary || item.summary) && (
          <>
            <Divider sx={{ my: 2.5 }} />
            <SectionTitle>Coverage</SectionTitle>
            <FieldGrid>
              <Field label="Devices">{dash(item.deviceSummary)}</Field>
              <Field label="Rooms">
                {/* The backend sends the literal string "room" when a
                    schedule's devices belong to no room — not a room name. */}
                {dash(item.roomSummary === "room" ? null : item.roomSummary)}
              </Field>
              {item.summary && <Field label="Summary">{item.summary}</Field>}
            </FieldGrid>
          </>
        )}

        <Divider sx={{ my: 2.5 }} />
        <SectionTitle>
          Devices &amp; actions ({actions.length})
        </SectionTitle>

        {actions.length === 0 ? (
          <Typography variant="body2" sx={{ color: "#94a3b8" }}>
            This schedule has no device actions.
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "1fr",
                sm: actions.length === 1 ? "1fr" : "repeat(2, minmax(0, 1fr))",
              },
            }}
          >
            {actions.map((act, i) => (
              <Box
                key={act._id || `${act.deviceId}-${i}`}
                sx={{
                  p: 2,
                  bgcolor: "white",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <Typography variant="body2" fontWeight={800}>
                  {act.deviceAlias || act.deviceName || "Unknown Device"}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    color: "#94a3b8",
                    fontFamily: "monospace",
                    wordBreak: "break-all",
                  }}
                >
                  {act.deviceId}
                </Typography>

                <Stack direction="row" spacing={1} mt={1.25} flexWrap="wrap" useFlexGap>
                  {/* ⚠️ deviceOn is nullable and null means "leave unchanged",
                      NOT off — do not fold it into the OFF label. */}
                  <Chip
                    label={
                      act.deviceOn === true
                        ? "POWER ON"
                        : act.deviceOn === false
                          ? "POWER OFF"
                          : "UNCHANGED"
                    }
                    size="small"
                    color={act.deviceOn === true ? "success" : "default"}
                    sx={{ fontWeight: 800, fontSize: "0.6rem" }}
                  />

                  {act.brightnessSupported && act.brightnessLevel != null && (
                    <Chip
                      label={`Brightness ${act.brightnessLevel}%`}
                      size="small"
                      sx={{ fontWeight: 800, fontSize: "0.6rem" }}
                    />
                  )}

                  {isHistory && act.executed != null && (
                    <Chip
                      label={act.executed ? "EXECUTED" : "NOT EXECUTED"}
                      size="small"
                      sx={{
                        fontWeight: 800,
                        fontSize: "0.6rem",
                        bgcolor: act.executed ? "#10b981" : "#ef4444",
                        color: "white",
                      }}
                    />
                  )}

                  {act.supported === false && (
                    <Chip
                      label="UNSUPPORTED"
                      size="small"
                      sx={{ fontWeight: 800, fontSize: "0.6rem" }}
                    />
                  )}
                </Stack>

                {isHistory && (act.executedAt || act.failureCount != null) && (
                  <Stack spacing={0.25} mt={1.25}>
                    {act.executedAt && (
                      <Typography variant="caption" sx={{ color: "#64748b" }}>
                        Executed {formatDisplay(act.executedAt)}
                      </Typography>
                    )}
                    {act.failureCount != null && act.failureCount > 0 && (
                      <Typography variant="caption" sx={{ color: "#b45309" }}>
                        {act.failureCount} failure
                        {act.failureCount === 1 ? "" : "s"}
                        {act.allowedRetries != null
                          ? ` of ${act.allowedRetries} allowed retries`
                          : ""}
                      </Typography>
                    )}
                    {act.errorCode != null && (
                      <Typography variant="caption" sx={{ color: "#b91c1c" }}>
                        Error code {act.errorCode}
                      </Typography>
                    )}
                  </Stack>
                )}

                {act.remark && (
                  <Typography
                    variant="caption"
                    sx={{ mt: 1, display: "block", color: "#ef4444" }}
                  >
                    {act.remark}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
